import { authInterceptorModule } from '../../interceptors/auth-interceptor/auth-interceptor';
import { allModelsModule } from '../../models/models';
import * as StringUtils from '../../utils/string-utils';
import { appConfig } from '../../constants/app-config';
import { jsDataModule } from '../../vendor/js-data';
import { storageServiceModule } from '../storage-service/storage-service';
import { isString } from '../../utils/type-utils';

export const accountServiceModule = angular
  .module('Q.services.account-service', [
    authInterceptorModule,
    allModelsModule,
    storageServiceModule,
    jsDataModule,
  ])

  .provider('accountService', function () {
    let _modelName = '';
    let STORAGE_KEY;
    let _oldStorageValue = null;
    let _includes = [];
    let logoutEjectExcludes = [];
    let _accountUrl = null;
    let _reload = true;
    let _temporary = false;
    let _skipLookup = false;

    let svc = null;

    return {
      //// Factory Configuration

      setModelName(modelName) {
        _modelName = modelName;
      },
      setTemporary(temporary) {
        _temporary = temporary;
      },
      setIncludes(includes) {
        _includes = includes;
      },
      setLogoutEjectExcludes(excludes) {
        logoutEjectExcludes = excludes;
      },
      setAccountUrl(accountUrl) {
        _accountUrl = accountUrl;
      },
      setSkipLookup(value) {
        _skipLookup = value;
      },
      getAccount() {
        return (svc && svc.get()) || {};
      },

      //// Factory Method

      $get(
        allModels,
        authInterceptor,
        $localStorage,
        $sessionStorage,
        $http,
        $q,
        $rootScope,
        $location,
        $window,
        $log,
        $timeout,
        DS,
      ) {
        const SESSIONS_API = appConfig.apiRoot + 'sessions';

        const LOGOUT_REASON_REGEX = /^QUALIFIED_LOGOUT!(\w+)!/;
        const LOGOUT_REASONS = {
          OTHER_TAB: 'Your account was logged out in a different window',
          INACTIVE: 'Your account was logged out due to inactivity',
        };
        const logoutReason = ($window.name.match(LOGOUT_REASON_REGEX) || [
          0,
          '',
        ])[1];

        function getStorageKey(model) {
          return 'accountService.' + StringUtils.lcFirst(model) + 'Id';
        }

        STORAGE_KEY = getStorageKey(_modelName);
        _oldStorageValue = $localStorage[STORAGE_KEY];

        const Model = DS.definitions[_modelName];
        const _current = {
          verified: false,
        };

        const readyDeferred = $q.defer();

        $rootScope.$on('$stateChangeSuccess', _updateAccountStorage);
        $rootScope.account = _current;

        const accountChangeListener = (function () {
          let unbindFn;
          return {
            enable() {
              if (!unbindFn) {
                unbindFn = listenForAccountChanges();
              }
            },
            disable() {
              // they may not have an unbindFn if the user continues
              // to use a token from before the code was updated
              if (unbindFn) {
                unbindFn();
                unbindFn = null;
              }
            },
          };
        })();

        $rootScope.$on('app.login', accountChangeListener.enable);
        $rootScope.$on('app.logout', accountChangeListener.disable);

        svc = {
          modelName: _modelName,
          model: Model,
          onLoginRedirectTo: null,
          get: getAccount,
          get loggedIn() {
            return !!_current.verified;
          },
          createAccount,
          setAccount,
          setLoginAccount,
          setPassword,
          lookupCurrent,
          forgotPassword,
          resetPassword,
          activateCheck,
          activate,
          confirm,
          prefs,
          savePrefs,
          login,
          setRemoteAccount: setAccountStorage,
          accountChangeListener,
          reLogin,
          logout,
          logoutAndReload,
          reloadAccount,
          isReady: false,
          whenReady,
          logoutReason,
          LOGOUT_REASONS,
        };

        readyDeferred.promise.then(() => (svc.isReady = true));

        return svc;

        /**
         * Generates the default includes when making an account request.
         * @returns {Array.<*>}
         * @private
         */
        function _getIncludes() {
          return [StringUtils.snakeCase(_modelName)].concat(_includes);
        }

        /**
         * Stores the current account info into $localStorage, updating the expiration
         * @private
         */
        function _updateAccountStorage() {
          if (_current.user && !_temporary) {
            setAccountStorage(_modelName, _current.user.id);
          }
        }

        /**
         * Returns the current account information
         * @returns {{verified: boolean, user: {Model}}}
         */
        function getAccount() {
          return _current;
        }

        /**
         * Stores the current account info, including the current user
         *
         * @param {*} user User object or ID
         * @returns {Promise} Promise if user was an ID
         */
        function setAccount(user) {
          if (angular.isObject(user)) {
            _current.user = user;
            _updateAccountStorage();
            _current.verified = true;

            $rootScope.$broadcast('app.accountSet', _current);

            readyDeferred.resolve(_current);
            return _current;
          }
        }

        /**
         * Sets the account and notifies that the user is logged in
         * @param {*} user
         * @param {*?} notifyOptions
         */
        function setLoginAccount(user, notifyOptions = {}) {
          setAccount(user);
          $rootScope.$broadcast(
            'app.login',
            angular.extend(
              {
                existing: true,
                account: getAccount(),
              },
              notifyOptions,
            ),
          );
        }

        /**
         * Looks up the current user based on the current token
         * @returns {Promise}
         */
        function lookupCurrent() {
          if (_skipLookup) {
            readyDeferred.resolve(_current);
            return $q.resolve(_current);
          }

          const data = $localStorage[STORAGE_KEY] || {};
          return $http
            .get(SESSIONS_API, {
              params: {
                includes: _includes,
                remember: data.rememberMe,
              },
              ignoreLoadingBar: true,
            })
            .then(function (userData) {
              const user = Model.inject(
                Model.deserialize(Model.name, userData),
              );
              setLoginAccount(user);
              return _current;
            })
            .catch((resp) => {
              if (
                resp.status === 0 ||
                (resp.status >= 500 && resp.status < 599)
              ) {
                // don't log out due to network errors
                readyDeferred.reject('Unable to load account');
                throw new Error('Unable to load account');
              } else {
                _logout();
                readyDeferred.resolve(_current);
              }
              return $q.reject(_current);
            });
        }

        /**
         * Changes the user's password
         * @param newPassword
         * @param oldPassword
         * @returns {Promise} Promise resolved when the password has been changed (or rejected on error)
         */
        function setPassword(newPassword, oldPassword) {
          if (!_current.user) return $q.reject();

          return $http.put(appConfig.apiRoot + 'account/password', {
            data: {
              new_password: newPassword,
              old_password: oldPassword,
            },
          });
        }

        /**
         * Sends off a forgot password email.
         * @param email
         * @returns {Promise}
         */
        function forgotPassword(email) {
          let redirectTo = `${_accountUrl}reset-password/:token`;
          if (svc.onLoginRedirectTo) {
            // use stored redirect
            redirectTo += `?stateName=${encodeURIComponent(svc.onLoginRedirectTo.stateName)}`;
            if (
              svc.onLoginRedirectTo.params &&
              Object.keys(svc.onLoginRedirectTo.params).length > 0
            ) {
              redirectTo += `&params=${encodeURIComponent(JSON.stringify(svc.onLoginRedirectTo.params))}`;
            }
          } else {
            // otherwise redirect to the current location, minus any "forgot-password/" at the end
            // of the URL.  It's a bit hacky, but it works.
            let url = $location.url().replace(/forgot-password\/?$/, '');
            if (url && url !== '/') {
              redirectTo += `?url=${encodeURIComponent(url)}`;
            }
          }
          return $http.post(appConfig.apiRoot + 'account/forgot_password', {
            data: {
              $type: Model.name,
              email,
              redirectTo,
            },
          });
        }

        /**
         * Resets the users password if we can find a valid token
         * @param token
         * @param password
         * @param options
         * @returns {Promise}
         */
        function resetPassword(token, password, options) {
          return $http
            .post(appConfig.apiRoot + 'account/reset_password', {
              data: {
                $type: Model.name,
                token,
                password,
                remember: options.rememberMe,
              },
              includes: _getIncludes(),
            })
            .then((res) => _finishLogin(res, options), _loginErrorHandler);
        }

        /**
         * Checks an activation token, returning the user's current data (if there is no error)
         * @param token
         * @returns {Promise}
         */
        function activateCheck(token) {
          if (!token)
            return $q.reject(new Error('Cannot activate without a token'));
          return $http
            .post(appConfig.apiRoot + 'account/activate_check', {
              data: { token },
              includes: _getIncludes(),
            })
            .then((res) => res && res.data && res.data.data);
        }

        /**
         * Activates an account
         * @param params {Object}
         * @param params.token
         * @param params.name
         * @param params.password
         * @param options
         * @returns {Promise}
         */
        function activate(params, options) {
          if (!params.token)
            return $q.reject(new Error('Cannot activate without a token'));
          return $http
            .post(appConfig.apiRoot + 'account/activate', {
              data: params,
              includes: _getIncludes(),
              remember: options.rememberMe,
            })
            .then((res) => _finishLogin(res, options), _loginErrorHandler);
        }

        /**
         * Confirms a user's email
         * @param token
         * @returns {Promise}
         */
        function confirm(token) {
          _reload = false;
          return $http
            .post(appConfig.apiRoot + 'account/confirm', {
              data: {
                token,
              },
              includes: _getIncludes(),
            })
            .then(
              (res) => _finishLogin(res, { showLoadingPanel: false }),
              _loginErrorHandler,
            );
        }

        /**
         * Get the current user preferences (or a dummy object if the user isn't logged in)
         * @returns {*}
         */
        function prefs() {
          if (_current.user) {
            if (!_current.user.uiPrefs) {
              _current.user.uiPrefs = {};
            }
            return _current.user.uiPrefs;
          } else {
            return {};
          }
        }

        /**
         * Saves the current user's preferences if the user has any changes.
         * Promise rejects if the user is not logged in
         * @returns {Promise}
         */
        function savePrefs(newPrefs) {
          if (!_current.user) {
            return $q.reject();
          } else {
            if (newPrefs) {
              angular.extend(prefs(), newPrefs);
            }
            if (!savePrefs.$promise) {
              let changes = Model.changes(_current.user);
              if (
                changes &&
                (changes.changed.uiPrefs || changes.added.uiPrefs)
              ) {
                // debounce changes
                savePrefs.$promise = $timeout(() => {
                  // delete the save promise before storing changes,
                  // so if a change comes in between we save it as a separate task
                  delete savePrefs.$promise;
                  return Model.update(
                    _current.user,
                    { uiPrefs: _current.user.uiPrefs },
                    { ignoreLoadingBar: true },
                  );
                }, 1000);
              }
            }
            // return save promise OR just resolve
            return savePrefs.$promise || $q.when(_current.user);
          }
        }

        /**
         * Logs the user in
         * @param email
         * @param password
         * @param options
         * @returns {Promise}
         */
        function login(email, password, options) {
          // clear authToken (if it exists) to allow for a clean login
          authInterceptor.clearAuthToken();
          return $http
            .post(
              SESSIONS_API,
              {
                data: {
                  $type: Model.name,
                  email: email,
                  password: password,
                  remember: options.rememberMe,
                },
                includes: _getIncludes(),
              },
              {
                alwaysPassLoginType: true,
              },
            )
            .then((res) => _finishLogin(res, options), _loginErrorHandler);
        }

        /**
         * Private method to complete the login process
         * @param res
         * @param options
         * @returns {*}
         * @private
         */
        function _finishLogin(res, options = {}) {
          const data = res.data;
          if (data && data.data && data.data.id) {
            setLoginAccount(
              Model.inject(Model.deserialize(Model.name, data)),
              options,
            );
            return getAccount();
          } else {
            return _loginErrorHandler(res);
          }
        }

        /**
         * Handles login server errors in a generic way
         * @param response The HTTP response
         * @returns {Promise}
         */
        function _loginErrorHandler(response) {
          return $q.reject(response);
        }

        /**
         * Creates a new account
         * @param emailOrAttributes A string with the user's email, or an object with extra attributes
         * @param options
         * @returns {Promise}
         */
        function createAccount(emailOrAttributes, options = {}) {
          if (angular.isString(emailOrAttributes)) {
            emailOrAttributes = { email: emailOrAttributes };
          }
          return Model.create(emailOrAttributes).then(function (user) {
            setLoginAccount(user, angular.extend({ existing: false }, options));
            return getAccount();
          });
        }

        function _logout(options = {}) {
          angular.forEach(_current, function (v, k) {
            delete _current[k];
          });
          _current.verified = false;
          delete $localStorage[STORAGE_KEY];
          _oldStorageValue = null;
          if (_modelName === 'TeamMember') {
            // also force a log out on the candidate model
            delete $localStorage[getStorageKey('Candidate')];
          }
          authInterceptor.clearAuthToken();
          if (options.ejectModels) {
            // clear all cached models on logout
            allModels.ejectAll({ exclude: logoutEjectExcludes });
          }
          $localStorage.$apply();
          let $event = $rootScope.$broadcast('app.logout', options);
          $localStorage.$apply();
          $sessionStorage.$apply();
          // delete the session on the server unless disabled
          const remoteLogout =
            options.destroySession === false
              ? $q.when()
              : $http.delete(SESSIONS_API);
          return remoteLogout.finally(() => {
            if (
              options.forceReload ||
              (options.reload !== false && $event.reload)
            ) {
              if (appConfig.resetOnLogout) {
                // force reload, but also clear path
                $window.location = isString(appConfig.resetOnLogout)
                  ? appConfig.resetOnLogout
                  : appConfig.appPath;
              } else {
                $window.location.reload();
              }
            }
          });
        }

        /**
         * Log out of the application
         * @param options
         */
        function logout(options) {
          if (!_current.verified) {
            // the user is already logged out, do not logout
            return $q.reject(true);
          }

          const $event = $rootScope.$broadcast('app.beforeLogout');
          if (!$event.defaultPrevented) {
            return _logout(options).then(angular.identity, angular.identity);
          } else if ($event.promise) {
            return $event.promise.then(() => _logout(options));
          } else {
            return $q.reject(false);
          }
        }

        /**
         * Log out of the application and forcibly reload
         */
        function logoutAndReload() {
          $rootScope.showLoadingPanel = true;
          return logout({ forceReload: true });
        }

        /**
         * Logs out of the application and then reloads using the cookie
         * This method is most useful for local impersonations/account switching
         * @param reload {boolean} If false, the calling function must reload the page with a new URL
         */
        function reLogin({ reload = true } = {}) {
          const baseAppUrl = $location.absUrl().replace($location.url(), '');

          return logout({ reload: false, destroySession: false }).finally(
            () => {
              if (reload) {
                $window.location.href = baseAppUrl;
              }
            },
          );
        }

        /**
         * Edits a localstorage value stored for a remote account
         * @param modelType The remote model type so we know which localstorage key to edit
         * @param id The user id
         */
        function setAccountStorage(modelType, id) {
          const key = getStorageKey(modelType);
          // we still track this in local storage so we can perform some smart checks
          $localStorage[key] = {
            id: id,
            lastActivity: Date.now(),
          };
          if (key === STORAGE_KEY) {
            _oldStorageValue = $localStorage[key];
          }
          $localStorage.$apply();
        }

        /**
         * Reloads the account on page load based on the auth token
         */
        function reloadAccount() {
          if (!_reload || _temporary) {
            readyDeferred.resolve(_current);
          } else if (authInterceptor.loggedIn()) {
            $rootScope.$broadcast('app.checkingLogin');
            lookupCurrent();
          } else {
            // make sure that we don't retain an invalid auth token at this point
            // because we're clearing an expired account, we don't need to eject the models
            // clearing them triggers a $digest, and we do _not_ want one at this stage
            _logout({ ejectModels: false, reload: false })
              .catch(angular.identity)
              .then(() => {
                readyDeferred.resolve(_current);
              });
          }
        }

        function whenReady() {
          return readyDeferred.promise;
        }

        function listenForAccountChanges() {
          $window.name = $window.name.replace(LOGOUT_REASON_REGEX, '');
          let lastCheck = Date.now();
          const windowFocusListener = () => {
            if (Date.now() - lastCheck > 60 * 60 * 1000) {
              // check to see if we can still log in if it's been an hour since we last checked
              $http
                .head(SESSIONS_API, { ignoreLoadingBar: true })
                .then(() => {
                  lastCheck = Date.now();
                })
                .catch((resp) => {
                  if (resp && resp.status === 401) {
                    $window.name = 'QUALIFIED_LOGOUT!INACTIVE!' + $window.name;
                    _logout();
                  }
                });
            }
          };
          angular.element($window).on('focus', windowFocusListener);
          const storageChangeListener = $rootScope.$watch(
            () => $localStorage[STORAGE_KEY],
            (newValue) => {
              // check if the user logged out, but is still verified in this window
              if (!newValue && _oldStorageValue && _current.verified) {
                // they logged out of another window so log out of this one
                window.name = 'QUALIFIED_LOGOUT!OTHER_TAB!' + window.name;
                _logout();
              } else if (
                newValue &&
                _oldStorageValue &&
                newValue.id !== _oldStorageValue.id &&
                newValue.lastActivity > _oldStorageValue.lastActivity
              ) {
                // check if the user account has changed
                // most likely they logged in from another window
                reLogin();
              } else if (
                !_oldStorageValue &&
                newValue &&
                newValue.id &&
                !_current.verified
              ) {
                // see if they logged in from another window when we were logged out
                reLogin({ reload: false }).then(() => {
                  $rootScope.$broadcast('app.accountSetRemotely', _current);
                });
              } else {
                _oldStorageValue = newValue;
              }
            },
          );

          return () => {
            angular.element($window).off('focus', windowFocusListener);
            storageChangeListener();
          };
        }
      },
    };
  })

  .run(function (accountService, $timeout) {
    $timeout(accountService.reloadAccount);
  });
